import React from 'react';
import { GeoJSON, Marker, Tooltip } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster/dist/index.js';
import { BpmMap } from './BpmMap';
import * as Constants from './constants';
import { DownloadsDecisionLocationsTable } from './DownloadsDecisionLocationsTable';
import { DownloadsUSRegionsTable } from './DownloadsUSRegionsTable';
import { STATES_DATA } from './states-data';

export const DownloadMap = ({
    rawData,
    sectionNumber,
    year,
    startMonth,
    endMonth,
    mapType
}) => {
    const noDataFound = <p>No download data for selected section and year.</p>;
    if (!rawData || rawData.length === 0) {
        return noDataFound;
    }

    // Filter by selected year and months
    const filteredData = rawData.filter(d =>
        d.year === year
        && d.month >= startMonth
        && d.month <= endMonth);

    const dataLookup = mapType === Constants.MAP_TYPE_US_REGIONS
        ? getRegionsToDownloads(filteredData)
        : getlocationToDownloads(filteredData);
    const mapContent = mapType === Constants.MAP_TYPE_US_REGIONS
        ? getStatesGeoJSON(dataLookup, sectionNumber, year, startMonth, endMonth)
        : getCitiesMarkers(dataLookup);
    const dataTable = mapType === Constants.MAP_TYPE_US_REGIONS
        ? <DownloadsUSRegionsTable regionToDownloads={dataLookup} />
        : <DownloadsDecisionLocationsTable locationToDownloads={dataLookup} />;

    return (
        <BpmMap
            mapContent={mapContent}
            dataTable={dataTable}
        />
    );
}

function getRegionsToDownloads(filteredData) {
    // Map each region to its total downloads so we can color the states accordingly.
    return filteredData.reduce((map, data) => {
        const region = data.region.toUpperCase();
        if (!region) {
            return map;
        }

        const regionTotalSoFar = map.get(region);
        map.set(region, data.downloads + (regionTotalSoFar || 0));
        return map;
    }, new Map());
}

function getStatesGeoJSON(regionToDownloads, sectionNumber, year, startMonth, endMonth) {
    // Because the tooltips bound to each feature (US state) are generated outside of React's component tree (using onEachFeature()), regenerate the component
    // each time the user's filter changes to make sure the tooltips are accurate.
    const filterKey = `${sectionNumber}::${year}::${startMonth}::${endMonth}`;
    return (
        <GeoJSON
            key={filterKey}
            data={STATES_DATA}
            style={feature => {
                return {
                    fillColor: getRegionFillColor(
                        feature.properties.name.toUpperCase(),
                        regionToDownloads),
                    weight: 1,
                    opacity: 1,
                    color: 'white',
                    fillOpacity: 0.7
                };
            }}
            onEachFeature={(feature, layer) => {
                if (feature && feature.properties && feature.properties.name) {
                    const downloads = regionToDownloads.get(feature.properties.name.toUpperCase());
                    if (downloads || downloads === 0) {
                        layer.bindTooltip(`${feature.properties.name}: ${downloads} download${downloads !== 1 ? "s" : ""}`);
                    }
                }
            }}
        />
    );
}

function getRegionFillColor(regionName, regionToDownloads) {
    const downloads = regionToDownloads.get(regionName);
    if (!downloads && downloads !== 0) {
        return 'transparent';
    } else if (downloads === 0) {
        return Constants.COLOR_0;
    } else if (downloads <= 2) {
        return Constants.COLOR_1_OUT_OF_5;
    } else if (downloads <= 4) {
        return Constants.COLOR_2_OUT_OF_5;
    } else if (downloads <= 6) {
        return Constants.COLOR_3_OUT_OF_5;
    } else if (downloads <= 8) {
        return Constants.COLOR_4_OUT_OF_5;
    } else {
        return Constants.COLOR_5_OUT_OF_5;
    }
}

function getlocationToDownloads(filteredData) {
    // Map each location to its total downloads so can create a marker for each location
    return filteredData.reduce((map, data) => {
        if (!data || data.latitude === undefined || data.longitude === undefined) {
            return map;
        }

        const key = `${data.latitude},${data.longitude}`;
        const cityDownloads = map.get(key);
        map.set(key,
            {
                country: data.country,
                region: data.region,
                city: data.city,
                downloads: data.downloads + (cityDownloads || 0)
            });
        return map;
    }, new Map());
}

function getCitiesMarkers(locationToDownloads) {
    let markers = [];
    for (const [key, value] of locationToDownloads) {
        const downloads = value.downloads;
        const icon = Constants.PIN_ICON_GOOD;
        markers.push(
            <Marker key={`marker-${key}`} position={key.split(",")} icon={icon} >
                <Tooltip key={`tooltip-${key}`}>{`${value.city}: ${downloads} download${downloads === 1 ? "" : "s"}`}</Tooltip>
            </Marker>
        );
    }

    return (
        <MarkerClusterGroup>
            {markers}
        </MarkerClusterGroup>
    );
}